<template>
  <div>
    <app-spinner  v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <h1 class="title">{{ car_data.car_model.name }} -  {{ car_data.plate_number }}</h1>
        <div class="status-title" :style="{ 'backgroundColor': car_data.status.color || '#5f5f5f' }">
          {{ car_data.status.name }}
        </div>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <button class="info-cards__control" @click="onOpenUpdateCarPopup">
            <img src="@/assets/img/edit-table-icon.svg" alt="">
          </button>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Car model:</span> <span>{{ car_data.car_model.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Plate number:</span> <span>{{ car_data.plate_number }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Driver:</span>
              <router-link
                v-if="car_data.driver"
                :to="{ name: 'employees-profile', params: { id: car_data.driver.id } }"
                class="info-cards__link"
              >
                {{ car_data.driver.first_name }} {{ car_data.driver.last_name }}
              </router-link>
              <span v-else>-</span>
            </div>
          </div>
        </div>
      </div>
      <app-tabs :tabs="tabs_to_show" />
      <transition name="fade" mode="out-in">
        <router-view  />
      </transition>
    </template>
    <modal
      name="update-car-popup"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('update-car-popup')" class="popup__close" type="button"></button>
        <div class="popup__title">Update car info</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckUpdateForm">
            <app-form-group label="Driver" required>
              <app-select
                v-model="form.driver"
                :reduce="item => item.id"
                :options="drivers_options"
                :clearable="false"
                label="full_name"
                placeholder="Enter driver"
                :error="$v.form.driver.$error"
              />
              <template #error>
                <div v-if="$v.form.driver.$dirty && !$v.form.driver.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Required licenses">
              <app-select
                v-model="form.required_licenses"
                :reduce="item => item.id"
                :options="license_options"
                :closeOnSelect="false"
                placeholder="Required licenses"
                multiple
              />
            </app-form-group>
            <app-form-group label="Required insurances">
              <app-select
                v-model="form.required_insurances"
                :reduce="item => item.id"
                :options="insurance_options"
                :closeOnSelect="false"
                placeholder="Required insurances"
                multiple
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="update" :disabled="$v.form.$error">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeInsuranceTypes,
  getAdministrativeLicenseTypes,
  getCarManagementCarsDetail,
  getEmployeesListSearch,
  patchCarManagementCar,
} from '@/http'
import AppTabs from '@/components/AppTabs'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'CarManagementCarsDetailLayout',
  components: { AppTabs },
  data() {
    return {
      is_loaded: false,
      tabs: [
        { name: 'car-management-cars-detail-licenses', label: 'Licenses', roles: ['general_manager', 'transfer_supervisor', 'transfer_manager'] },
        { name: 'car-management-cars-detail-insurances', label: 'Insurances', roles: ['general_manager', 'transfer_supervisor', 'transfer_manager'] },
        { name: 'car-management-cars-detail-odometer', label: 'Odometer history', roles: ['general_manager'] },
      ],
      tabs_to_show: [],
      car_data: {},
      form: {},
      drivers_options: [],
      license_options: [],
      insurance_options: [],
    }
  },
  validations: {
    form: {
      driver: { required }
    }
  },
  created() {
    this.getCarDetail()
    this.getDriverOptions()
    getAdministrativeLicenseTypes().then(response => { this.license_options = response.data })
    getAdministrativeInsuranceTypes().then(response => { this.insurance_options = response.data })
    this.tabs.forEach(item => {
      if (!item.roles.length) {
        this.tabs_to_show.push(item)
        return
      }
      item.roles.forEach(role => {
        if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) this.tabs_to_show.push(item)
      })
    })
  },
  methods: {
    getCarDetail() {
      getCarManagementCarsDetail(this.$route.params.id)
        .then(response => {
          this.car_data = response.data
          document.title = `Car (${ this.car_data.car_model.name } (${ this.car_data.plate_number })) | Altezza Board`
          this.is_loaded = true
        })
    },
    getDriverOptions() {
      getEmployeesListSearch({
        position: 'driver',
        employment_status: 'working',
      })
        .then(response => {
          this.drivers_options = response.data
        })
    },
    onOpenUpdateCarPopup() {
      this.form = {
        driver: this.car_data.driver?.id,
        required_licenses: this.car_data.required_licenses,
        required_insurances: this.car_data.required_insurances
      }
      this.$v.form.$reset()
      this.$modal.show('update-car-popup')
    },
    onCheckUpdateForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendUpdateForm()
      }
    },
    sendUpdateForm() {
      this.$refs.update.preload = true
      patchCarManagementCar(this.$route.params.id, this.form)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Car saved',
          })
          this.getCarDetail()
          this.getDriverOptions()
          this.$modal.hide('update-car-popup')
        })
        .catch(() => { this.$refs.update.preload = false })
    }
  }
}
</script>
